import * as turf from '@turf/turf';
import type mapboxgl from 'mapbox-gl';
import { HDDRoute, ProfilePoint, SurfaceFeature, GeologyLayer } from './types';
import {
  estimateBuildingHeight,
  estimateBuildingMass,
  estimateFoundationDepth,
  estimateWaterwayWidth,
  estimateWaterDepth,
  estimateRoadWidth,
  estimateRailwayWidth,
  estimateRoadPavementDepth,
  estimateRoadUtilityDepth,
  getRoadUtilityInfo,
  getBuildingFootprintArea
} from './buildingEstimation';

// Mapbox access token - must match the one in main.ts
const MAPBOX_TOKEN = 'pk.eyJ1IjoiYmFydG9uaXEiLCJhIjoiY21tMjc0cnUwMDRlaDJ1cjB1cWFoeXdjeCJ9.6pGhbJCs7SRc3r24kunmNA';

// Cache for terrain tiles to avoid re-fetching
const tileCache: Map<string, ImageData> = new Map();
const bathymetryCache: Map<string, ImageData> = new Map();

// Cache for BGS geology queries (by bounding box)
const geologyCache: Map<string, any[]> = new Map();

// Cache for US geology queries (Macrostrat and SSURGO)
const macrostratCache: Map<string, any> = new Map();
const ssurgoCache: Map<string, any> = new Map();
const depElevationCache: Map<string, number> = new Map();

/**
 * Fetch with timeout wrapper
 */
async function fetchWithTimeout(url: string, timeoutMs: number = 10000): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeoutMs}ms`);
    }
    throw error;
  }
}

// Color mapping for common rock types - using more distinct colors
const GEOLOGY_COLORS: Record<string, string> = {
  // Superficial deposits - warm colors
  'SAND AND GRAVEL': '#FFD700',  // Gold
  'SAND': '#FFEC8B',             // Light goldenrod
  'GRAVEL': '#DAA520',           // Goldenrod
  'CLAY': '#CD853F',             // Peru (orange-brown)
  'SILT': '#DEB887',             // Burlywood
  'PEAT': '#8B4513',             // Saddle brown
  'ALLUVIUM': '#F4A460',         // Sandy brown
  'TILL': '#BC8F8F',             // Rosy brown
  'GLACIAL': '#D2B48C',          // Tan
  'RIVER TERRACE': '#E9967A',    // Dark salmon
  'HEAD': '#C4A777',             // Khaki-ish
  'BLOWN SAND': '#FFFACD',       // Lemon chiffon
  // Bedrock - cooler colors
  'CHALK': '#F5F5F5',            // White smoke
  'LIMESTONE': '#E0E0E0',        // Light gray
  'SANDSTONE': '#F4A460',        // Sandy brown
  'MUDSTONE': '#708090',         // Slate gray
  'CLAYSTONE': '#778899',        // Light slate gray
  'SHALE': '#696969',            // Dim gray
  'GRANITE': '#FFB6C1',          // Light pink
  'BASALT': '#2F4F4F',           // Dark slate gray
  'CRAG': '#E6C9A8',             // Crag deposits (East Anglia)
  'LONDON CLAY': '#8B7355',      // Brown
  'GAULT': '#6B8E23',            // Olive drab
  'GREENSAND': '#9ACD32',        // Yellow green
  'DEFAULT': '#D3D3D3'           // Light gray
};

// US Geology colors (Macrostrat lithology types)
const US_GEOLOGY_COLORS: Record<string, string> = {
  // Sedimentary rocks
  'sandstone': '#F4A460',        // Sandy brown
  'shale': '#696969',            // Dim gray
  'limestone': '#E0E0E0',        // Light gray
  'dolomite': '#D8BFD8',         // Thistle
  'conglomerate': '#DEB887',     // Burlywood
  'siltstone': '#A9A9A9',        // Dark gray
  'mudstone': '#708090',         // Slate gray
  'chalk': '#F5F5F5',            // White smoke
  'chert': '#8B8B83',            // Ivory4
  'coal': '#2F2F2F',             // Very dark gray
  'evaporite': '#FFE4E1',        // Misty rose
  'marl': '#C4B8A8',             // Warm gray
  // Igneous rocks
  'granite': '#FFB6C1',          // Light pink
  'basalt': '#2F4F4F',           // Dark slate gray
  'rhyolite': '#DDA0DD',         // Plum
  'andesite': '#808080',         // Gray
  'gabbro': '#4A4A4A',           // Dark gray
  'diorite': '#696969',          // Dim gray
  'volcanic': '#8B0000',         // Dark red
  'tuff': '#D2B48C',             // Tan
  'obsidian': '#1A1A1A',         // Nearly black
  // Metamorphic rocks
  'gneiss': '#9370DB',           // Medium purple
  'schist': '#6A5ACD',           // Slate blue
  'quartzite': '#FFFAF0',        // Floral white
  'marble': '#FAFAFA',           // Near white
  'slate': '#708090',            // Slate gray
  'phyllite': '#778899',         // Light slate gray
  'metamorphic': '#9370DB',      // Medium purple
  // Unconsolidated
  'alluvium': '#FFD700',         // Gold
  'gravel': '#DAA520',           // Goldenrod
  'sand': '#FFEC8B',             // Light goldenrod
  'clay': '#CD853F',             // Peru
  'silt': '#DEB887',             // Burlywood
  'till': '#BC8F8F',             // Rosy brown
  'loess': '#F5DEB3',            // Wheat
  'DEFAULT': '#D3D3D3'           // Light gray
};

// SSURGO soil texture colors
const SOIL_COLORS: Record<string, string> = {
  // Sandy soils - yellows/golds
  'sand': '#FFEC8B',
  'loamy sand': '#FFD700',
  'sandy loam': '#F0E68C',
  // Loamy soils - browns/tans
  'loam': '#D2B48C',
  'sandy clay loam': '#DEB887',
  'clay loam': '#C4A777',
  'silty clay loam': '#B8A080',
  'silt loam': '#D2B48C',
  'silt': '#E8DCC8',
  // Clay soils - darker browns/reds
  'clay': '#8B4513',
  'sandy clay': '#A0522D',
  'silty clay': '#8B6914',
  // Organic soils
  'muck': '#3D2B1F',
  'peat': '#4A3728',
  'mucky': '#4D4033',
  // Special
  'bedrock': '#808080',
  'water': '#4169E1',
  'DEFAULT': '#D3D3D3'
};

/**
 * Get color for a rock type (UK BGS)
 */
function getGeologyColor(rockType: string): string {
  if (!rockType || rockType === 'Unknown') {
    return GEOLOGY_COLORS['DEFAULT'];
  }
  const upperRock = rockType.toUpperCase();
  for (const [key, color] of Object.entries(GEOLOGY_COLORS)) {
    if (upperRock.includes(key)) {
      return color;
    }
  }
  return GEOLOGY_COLORS['DEFAULT'];
}

/**
 * Get color for US rock type (Macrostrat)
 */
function getUSGeologyColor(lithology: string): string {
  if (!lithology || lithology === 'Unknown') {
    return US_GEOLOGY_COLORS['DEFAULT'];
  }
  const lowerLith = lithology.toLowerCase();
  for (const [key, color] of Object.entries(US_GEOLOGY_COLORS)) {
    if (lowerLith.includes(key)) {
      return color;
    }
  }
  return US_GEOLOGY_COLORS['DEFAULT'];
}

/**
 * Get color for soil texture (SSURGO)
 */
function getSoilColor(texture: string): string {
  if (!texture) {
    return SOIL_COLORS['DEFAULT'];
  }
  const lowerTexture = texture.toLowerCase();
  for (const [key, color] of Object.entries(SOIL_COLORS)) {
    if (lowerTexture.includes(key)) {
      return color;
    }
  }
  return SOIL_COLORS['DEFAULT'];
}

/**
 * Check if coordinates are within UK bounds
 */
function isUKCoordinates(lng: number, lat: number): boolean {
  return lng >= -9 && lng <= 3 && lat >= 49 && lat <= 61;
}

/**
 * Check if coordinates are within US bounds (conterminous US)
 */
function isUSCoordinates(lng: number, lat: number): boolean {
  return lng >= -125 && lng <= -66 && lat >= 24 && lat <= 50;
}

/**
 * Fetch bedrock geology from Macrostrat API (US/Global)
 */
async function fetchMacrostratGeology(
  lng: number,
  lat: number
): Promise<GeologyLayer | undefined> {
  const cacheKey = `${lng.toFixed(4)},${lat.toFixed(4)}`;

  if (macrostratCache.has(cacheKey)) {
    return macrostratCache.get(cacheKey);
  }

  const url = `https://macrostrat.org/api/v2/geologic_units/map?lat=${lat}&lng=${lng}&scale=medium`;

  try {
    const response = await fetchWithTimeout(url, 10000);
    if (!response.ok) {
      console.warn('Macrostrat API error:', response.status);
      return undefined;
    }

    const data = await response.json();
    const features = data.success?.data || [];

    if (features.length === 0) {
      macrostratCache.set(cacheKey, undefined);
      return undefined;
    }

    // Find the first feature with actual lithology data (skip "water", etc.)
    let feature = features[0];
    for (const f of features) {
      if (f.lith && f.lith !== '' && f.name !== 'water') {
        feature = f;
        break;
      }
    }

    const lithology = feature.lith || feature.name || 'Unknown';

    // Skip if this is just water with no geology data
    if (lithology === 'water' || lithology === '') {
      macrostratCache.set(cacheKey, undefined);
      return undefined;
    }

    const layer: GeologyLayer = {
      type: 'bedrock',
      code: feature.unit_id?.toString() || feature.map_id?.toString() || '',
      description: feature.name || feature.strat_name || lithology,
      rockType: lithology,
      age: feature.best_int_name || feature.t_int_name || feature.b_int_name,
      color: feature.color || getUSGeologyColor(lithology),
      source: 'macrostrat'
    };

    macrostratCache.set(cacheKey, layer);
    return layer;
  } catch (error: any) {
    console.warn('Macrostrat fetch failed:', error.message || error);
    return undefined;
  }
}

/**
 * Fetch soil data from SSURGO (USDA NRCS)
 *
 * STATUS: Disabled due to API issues (no API key needed - it's free)
 *
 * Issues encountered:
 * 1. Direct SSURGO spatial query (SDA_Get_Mukey_from_intersection_with_WktWgs84)
 *    returns 400 Bad Request - query format is very particular
 * 2. SoilWeb API (UC Davis wrapper) works but has no CORS headers,
 *    so browser blocks the request
 *
 * Future options:
 * - Set up a server-side proxy to call SoilWeb API
 * - Debug the exact SSURGO SQL query format required
 * - Use SSURGO WCS (Web Coverage Service) for mukey grids
 */
async function fetchSSURGOSoil(
  lng: number,
  lat: number
): Promise<GeologyLayer | undefined> {
  // Temporarily disabled - return undefined
  // US routes will show Macrostrat bedrock data only
  return undefined;

  /* COMMENTED OUT - SoilWeb approach (CORS blocked):
  const cacheKey = `${lng.toFixed(4)},${lat.toFixed(4)}`;

  if (ssurgoCache.has(cacheKey)) {
    return ssurgoCache.get(cacheKey);
  }

  try {
    // Step 1: Get mukey from SoilWeb API (UC Davis)
    // This API works but doesn't have CORS headers
    const soilWebUrl = `https://casoilresource.lawr.ucdavis.edu/soil_web/reflector_api/soils.php?what=mapunit&lon=${lng}&lat=${lat}`;

    const soilWebResponse = await fetchWithTimeout(soilWebUrl, 8000);
    if (!soilWebResponse.ok) {
      ssurgoCache.set(cacheKey, undefined);
      return undefined;
    }

    const soilWebData = await soilWebResponse.json();

    if (!soilWebData || !soilWebData.mukey) {
      ssurgoCache.set(cacheKey, undefined);
      return undefined;
    }

    const mukey = soilWebData.mukey;
    const muname = soilWebData['Map Unit Name'] || soilWebData.muname || '';

    // Step 2: Get detailed soil properties from SSURGO using mukey
    const query = `
      SELECT TOP 1
        c.compname,
        c.comppct_r,
        c.drainagecl,
        ch.hzdept_r,
        ch.hzdepb_r,
        ch.texdesc
      FROM component c
      LEFT JOIN chorizon ch ON ch.cokey = c.cokey
      WHERE c.mukey = '${mukey}'
      ORDER BY c.comppct_r DESC, ch.hzdept_r ASC
    `.trim();

    const encodedQuery = encodeURIComponent(query);
    const postBody = `query=${encodedQuery}&format=json%2Bcolumnname%2Bmetadata`;

    const ssurgoResponse = await fetch(
      'https://sdmdataaccess.sc.egov.usda.gov/tabular/post.rest',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: postBody
      }
    );

    let texture = 'Unknown';
    let drainagecl = '';
    let thickness: number | undefined;

    if (ssurgoResponse.ok) {
      const ssurgoData = await ssurgoResponse.json();
      if (ssurgoData.Table && ssurgoData.Table.length > 0) {
        const row = ssurgoData.Table[0];
        texture = row[5] || row[0] || 'Unknown';
        drainagecl = row[2] || '';
        const topDepth = row[3];
        const bottomDepth = row[4];
        if (topDepth !== null && bottomDepth !== null) {
          thickness = (bottomDepth - topDepth) / 100;
        }
      }
    }

    const layer: GeologyLayer = {
      type: 'soil',
      code: mukey.toString(),
      description: muname || texture,
      rockType: texture,
      color: getSoilColor(texture),
      source: 'ssurgo',
      thickness,
      drainageClass: drainagecl
    };

    ssurgoCache.set(cacheKey, layer);
    return layer;
  } catch (error: any) {
    ssurgoCache.set(cacheKey, undefined);
    return undefined;
  }
  */
}

/**
 * Fetch elevation from USGS 3DEP (higher resolution than Mapbox for US)
 * Uses the EPQS (Elevation Point Query Service) API
 *
 * STATUS: Disabled - too slow for per-point queries, using Mapbox instead
 * No API key needed - it's a free USGS service
 */
async function get3DEPElevation(_lng: number, _lat: number): Promise<number | null> {
  // Disabled - 3DEP is slow and causes long load times
  // Mapbox elevation works fine for US routes
  return null;

  /* COMMENTED OUT - 3DEP implementation:
  const cacheKey = `${lng.toFixed(5)},${lat.toFixed(5)}`;

  if (depElevationCache.has(cacheKey)) {
    return depElevationCache.get(cacheKey)!;
  }

  // USGS EPQS API v1 - note: x=longitude, y=latitude
  const url = `https://epqs.nationalmap.gov/v1/json?x=${lng}&y=${lat}&wkid=4326&units=Meters&includeDate=false`;

  try {
    // Use longer timeout as USGS service can be slow
    const response = await fetchWithTimeout(url, 15000);
    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const elevation = data.value;

    if (elevation === null || elevation === undefined || elevation === -1000000) {
      // -1000000 is the no-data value for EPQS
      return null;
    }

    depElevationCache.set(cacheKey, elevation);
    return elevation;
  } catch (error: any) {
    return null;
  }
  */
}

/**
 * Query US geology at a point (Macrostrat + SSURGO)
 */
async function queryUSGeologyAtPoint(
  lng: number,
  lat: number
): Promise<{ bedrock?: GeologyLayer; soil?: GeologyLayer }> {
  const [bedrock, soil] = await Promise.all([
    fetchMacrostratGeology(lng, lat),
    fetchSSURGOSoil(lng, lat)
  ]);

  return { bedrock, soil };
}

/**
 * Fetch elevation from Mapbox Terrain-RGB tiles API
 * This works independently of what's visible on the map
 */
async function getElevationFromAPI(lng: number, lat: number): Promise<number> {
  const zoom = 14; // Good balance of resolution and tile size

  // Convert lng/lat to tile coordinates
  const x = Math.floor((lng + 180) / 360 * Math.pow(2, zoom));
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * Math.pow(2, zoom));

  // Calculate pixel position within tile (256x256)
  const tileSize = 256;
  const pixelX = Math.floor(((lng + 180) / 360 * Math.pow(2, zoom) - x) * tileSize);
  const pixelY = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * Math.pow(2, zoom) - y) * tileSize);

  const tileKey = `${zoom}/${x}/${y}`;

  try {
    let imageData = tileCache.get(tileKey);

    if (!imageData) {
      // Fetch the terrain RGB tile
      const url = `https://api.mapbox.com/v4/mapbox.terrain-rgb/${tileKey}.pngraw?access_token=${MAPBOX_TOKEN}`;
      const response = await fetchWithTimeout(url, 10000);

      if (!response.ok) {
        console.error('Terrain API error:', response.status);
        return 0;
      }

      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);

      // Draw to canvas to get pixel data
      const canvas = new OffscreenCanvas(tileSize, tileSize);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(bitmap, 0, 0);
      imageData = ctx.getImageData(0, 0, tileSize, tileSize);

      // Cache the tile
      tileCache.set(tileKey, imageData);
    }

    // Get pixel at the calculated position
    const idx = (pixelY * tileSize + pixelX) * 4;
    const r = imageData.data[idx];
    const g = imageData.data[idx + 1];
    const b = imageData.data[idx + 2];

    // Decode elevation from RGB
    // Formula: elevation = -10000 + ((R * 256 * 256 + G * 256 + B) * 0.1)
    const elevation = -10000 + ((r * 256 * 256 + g * 256 + b) * 0.1);

    return elevation;
  } catch (error) {
    console.error('Error fetching elevation:', error);
    return 0;
  }
}

/**
 * Fetch bathymetric depth from Mapbox Bathymetry tileset
 * Returns depth below water surface (positive value) or null if no data
 */
async function getBathymetryDepth(lng: number, lat: number): Promise<number | null> {
  const zoom = 10; // Bathymetry tileset has lower resolution

  // Convert lng/lat to tile coordinates
  const x = Math.floor((lng + 180) / 360 * Math.pow(2, zoom));
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * Math.pow(2, zoom));

  // Calculate pixel position within tile (256x256)
  const tileSize = 256;
  const pixelX = Math.floor(((lng + 180) / 360 * Math.pow(2, zoom) - x) * tileSize);
  const pixelY = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * Math.pow(2, zoom) - y) * tileSize);

  const tileKey = `${zoom}/${x}/${y}`;

  try {
    let imageData = bathymetryCache.get(tileKey);

    if (!imageData) {
      // Fetch the bathymetry tile
      const url = `https://api.mapbox.com/v4/mapbox.mapbox-bathymetry-v2/${tileKey}.pngraw?access_token=${MAPBOX_TOKEN}`;
      const response = await fetchWithTimeout(url, 10000);

      if (!response.ok) {
        // Bathymetry data may not be available for all areas
        return null;
      }

      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);

      // Draw to canvas to get pixel data
      const canvas = new OffscreenCanvas(tileSize, tileSize);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(bitmap, 0, 0);
      imageData = ctx.getImageData(0, 0, tileSize, tileSize);

      // Cache the tile
      bathymetryCache.set(tileKey, imageData);
    }

    // Get pixel at the calculated position
    const idx = (pixelY * tileSize + pixelX) * 4;
    const r = imageData.data[idx];
    const g = imageData.data[idx + 1];
    const b = imageData.data[idx + 2];
    const a = imageData.data[idx + 3];

    // Check if this is a valid data pixel (not transparent/no-data)
    if (a === 0 || (r === 0 && g === 0 && b === 0)) {
      return null;
    }

    // Decode depth from RGB (same formula as terrain-rgb)
    // Negative values indicate depth below sea level
    const elevation = -10000 + ((r * 256 * 256 + g * 256 + b) * 0.1);

    // Return as positive depth (below surface)
    // Bathymetry data shows seabed elevation (negative = below sea level)
    if (elevation < 0) {
      return Math.abs(elevation);
    }

    return null; // Not underwater
  } catch (error) {
    console.error('Error fetching bathymetry:', error);
    return null;
  }
}

/**
 * Query BGS geology for a bounding box and cache the results
 */
async function fetchBGSGeology(
  collection: 'bgsgeology625ksuperficial' | 'bgsgeology625kbedrock',
  bbox: [number, number, number, number]
): Promise<any[]> {
  const cacheKey = `${collection}:${bbox.join(',')}`;

  if (geologyCache.has(cacheKey)) {
    return geologyCache.get(cacheKey)!;
  }

  const url = `https://ogcapi.bgs.ac.uk/collections/${collection}/items?f=json&limit=100&bbox=${bbox.join(',')}`;

  try {
    const response = await fetchWithTimeout(url, 8000); // 8 second timeout for BGS
    if (!response.ok) {
      console.error('BGS API error:', response.status);
      return [];
    }

    const data = await response.json();
    const features = data.features || [];

    // Cache the results
    geologyCache.set(cacheKey, features);
    return features;
  } catch (error: any) {
    // Don't let BGS failures block the profile generation
    console.warn('BGS geology fetch failed (continuing without):', error.message || error);
    return [];
  }
}

/**
 * Find which geology polygon contains a point
 */
function findGeologyAtPoint(
  features: any[],
  lng: number,
  lat: number,
  type: 'superficial' | 'bedrock'
): GeologyLayer | undefined {
  const point = turf.point([lng, lat]);

  for (const feature of features) {
    if (!feature.geometry) continue;

    try {
      // Check if point is inside the polygon
      const isInside = turf.booleanPointInPolygon(point, feature.geometry);

      if (isInside) {
        const props = feature.properties || {};

        // Try multiple fields to get rock type
        let rockType = props.rock_d || props.rock || '';
        const description = props.lex_d || props.lex || '';

        // If rock type is empty, try to extract from description
        if (!rockType && description) {
          // Common patterns in BGS descriptions
          if (description.toUpperCase().includes('CHALK')) rockType = 'Chalk';
          else if (description.toUpperCase().includes('SAND AND GRAVEL')) rockType = 'Sand and Gravel';
          else if (description.toUpperCase().includes('SANDSTONE')) rockType = 'Sandstone';
          else if (description.toUpperCase().includes('MUDSTONE')) rockType = 'Mudstone';
          else if (description.toUpperCase().includes('LIMESTONE')) rockType = 'Limestone';
          else if (description.toUpperCase().includes('CLAY')) rockType = 'Clay';
          else if (description.toUpperCase().includes('ALLUVIUM')) rockType = 'Alluvium';
          else if (description.toUpperCase().includes('PEAT')) rockType = 'Peat';
          else if (description.toUpperCase().includes('TILL')) rockType = 'Till';
          else if (description.toUpperCase().includes('CRAG')) rockType = 'Crag';
          else if (description.toUpperCase().includes('LONDON CLAY')) rockType = 'London Clay';
          else if (description.toUpperCase().includes('GAULT')) rockType = 'Gault';
          else if (description.toUpperCase().includes('GREENSAND')) rockType = 'Greensand';
          else rockType = description.split(' ')[0] || 'Unknown'; // Use first word as fallback
        }

        if (!rockType) rockType = 'Unknown';

        return {
          type,
          code: props.lex || props.bgsref || '',
          description: description || rockType,
          rockType,
          age: props.max_age || props.min_age,
          color: getGeologyColor(rockType)
        };
      }
    } catch (e) {
      // Skip invalid geometries
      continue;
    }
  }

  return undefined;
}

/**
 * Query BGS for geology at a specific point
 */
async function queryGeologyAtPoint(
  lng: number,
  lat: number
): Promise<{ superficial?: GeologyLayer; bedrock?: GeologyLayer }> {
  // Create a small bounding box around the point (roughly 500m)
  const buffer = 0.005; // ~500m at UK latitudes
  const bbox: [number, number, number, number] = [
    lng - buffer,
    lat - buffer,
    lng + buffer,
    lat + buffer
  ];

  // Fetch both superficial and bedrock geology
  const [superficialFeatures, bedrockFeatures] = await Promise.all([
    fetchBGSGeology('bgsgeology625ksuperficial', bbox),
    fetchBGSGeology('bgsgeology625kbedrock', bbox)
  ]);

  return {
    superficial: findGeologyAtPoint(superficialFeatures, lng, lat, 'superficial'),
    bedrock: findGeologyAtPoint(bedrockFeatures, lng, lat, 'bedrock')
  };
}

export async function generateElevationProfile(
  map: mapboxgl.Map,
  route: HDDRoute,
  sampleInterval: number = 10
): Promise<ProfilePoint[]> {
  const points: ProfilePoint[] = [];
  const numSamples = Math.ceil(route.distance / sampleInterval);

  // Pre-calculate all sample points
  const samplePoints: { distance: number; coords: [number, number] }[] = [];
  for (let i = 0; i <= numSamples; i++) {
    const distance = Math.min(i * sampleInterval, route.distance);
    const point = turf.along(route.path, distance / 1000, { units: 'kilometers' });
    samplePoints.push({
      distance,
      coords: point.geometry.coordinates as [number, number]
    });
  }

  // Detect region based on start coordinates
  const startCoords = samplePoints[0].coords;
  const isUK = isUKCoordinates(startCoords[0], startCoords[1]);
  const isUS = isUSCoordinates(startCoords[0], startCoords[1]);

  console.log(`Route region detected: ${isUK ? 'UK' : isUS ? 'US' : 'Other'}`);

  let superficialFeatures: any[] = [];
  let bedrockFeatures: any[] = [];

  // Pre-fetch BGS geology for UK routes
  if (isUK) {
    // Calculate route bounding box with buffer
    const lngs = samplePoints.map(p => p.coords[0]);
    const lats = samplePoints.map(p => p.coords[1]);
    const buffer = 0.01; // ~1km buffer
    const routeBbox: [number, number, number, number] = [
      Math.min(...lngs) - buffer,
      Math.min(...lats) - buffer,
      Math.max(...lngs) + buffer,
      Math.max(...lats) + buffer
    ];

    // Fetch geology for entire route at once
    console.log('Fetching BGS geology for route...');
    try {
      [superficialFeatures, bedrockFeatures] = await Promise.all([
        fetchBGSGeology('bgsgeology625ksuperficial', routeBbox),
        fetchBGSGeology('bgsgeology625kbedrock', routeBbox)
      ]);
      console.log(`BGS geology loaded: ${superficialFeatures.length} superficial, ${bedrockFeatures.length} bedrock features`);
    } catch (error) {
      console.warn('BGS prefetch failed, continuing without geology');
    }
  }

  // For US routes, log available data sources
  if (isUS) {
    console.log('Using US data sources: Macrostrat (bedrock)');
  }

  // Process each sample point
  for (const sample of samplePoints) {
    const coords = sample.coords;

    // Get elevation from Terrain-RGB API (works regardless of map viewport)
    let elevation = await getElevationFromAPI(coords[0], coords[1]);
    let elevation3DEP: number | undefined;

    // For US coordinates, also try to get higher-resolution 3DEP elevation
    if (isUS) {
      const dep = await get3DEPElevation(coords[0], coords[1]);
      if (dep !== null) {
        elevation3DEP = dep;
        // Use 3DEP as primary elevation if available (higher resolution)
        elevation = dep;
      }
    }

    // Query features at this point
    const features = await querySurfaceFeatures(map, coords[0], coords[1]);

    // Check if this point is over water
    const waterFeature = features.find(f => f.type === 'water');
    let waterSurfaceElevation: number | undefined;

    if (waterFeature && waterFeature.depth) {
      // Store the water surface elevation (what terrain API returns for water)
      waterSurfaceElevation = elevation;
      // Adjust elevation to show seabed (ground you'd drill through)
      elevation = elevation - waterFeature.depth;
    }

    // Look up geology based on region
    let geology: { superficial?: GeologyLayer; bedrock?: GeologyLayer; soil?: GeologyLayer } | undefined;

    if (isUK) {
      // UK: Use pre-fetched BGS data
      geology = {
        superficial: findGeologyAtPoint(superficialFeatures, coords[0], coords[1], 'superficial'),
        bedrock: findGeologyAtPoint(bedrockFeatures, coords[0], coords[1], 'bedrock')
      };
    } else if (isUS) {
      // US: Query Macrostrat (bedrock) and SSURGO (soil) per point
      const usGeology = await queryUSGeologyAtPoint(coords[0], coords[1]);
      geology = {
        bedrock: usGeology.bedrock,
        soil: usGeology.soil
      };
    }

    points.push({
      distance: sample.distance,
      longitude: coords[0],
      latitude: coords[1],
      elevation,
      elevation3DEP,
      waterSurfaceElevation,
      surfaceFeatures: features,
      geology
    });
  }

  return points;
}

async function querySurfaceFeatures(
  map: mapboxgl.Map,
  lng: number,
  lat: number
): Promise<SurfaceFeature[]> {
  const features: SurfaceFeature[] = [];

  // Use Tilequery API to get features from vector tiles directly
  // This works regardless of zoom level or what's currently rendered
  // Include 'water' layer for large bodies of water (estuaries, lakes, seas)
  // Include 'road' and 'railway' layers for infrastructure crossings
  // Use small radius (2m) to only detect features the route actually passes through
  const radius = 2; // meters - small radius for accuracy
  const url = `https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/tilequery/${lng},${lat}.json?radius=${radius}&layers=building,waterway,water,road,railway&limit=10&access_token=${MAPBOX_TOKEN}`;

  try {
    const response = await fetchWithTimeout(url, 10000);

    if (!response.ok) {
      console.error('Tilequery API error:', response.status, response.statusText);
      return features;
    }

    const data = await response.json();

    for (const feature of data.features || []) {
      const props = feature.properties || {};
      const layerName = props.tilequery?.layer;
      const distance = props.tilequery?.distance || 0;

      // For buildings, only include if within 1.5m of the route line
      if (layerName === 'building' && distance <= 1.5) {
        // Create a feature object for estimation functions
        // Note: Tilequery returns Point geometries, not building polygons
        const buildingFeature = {
          geometry: feature.geometry,
          properties: props
        };

        const height = props.height || estimateBuildingHeight(buildingFeature);
        // Use getBuildingFootprintArea which handles Point geometries from Tilequery
        const area = getBuildingFootprintArea(buildingFeature);
        const volume = area * height;
        const mass = estimateBuildingMass(volume, props.type);
        const foundationDepth = estimateFoundationDepth(height, mass, area);

        features.push({
          type: 'building',
          name: props.name,
          height,
          mass,
          foundationDepth,
          properties: props
        });
      } else if (layerName === 'waterway' && distance <= 2) {
        features.push({
          type: 'waterway',
          name: props.name,
          width: estimateWaterwayWidth(props.class || 'stream'),
          properties: props
        });
      } else if (layerName === 'water' && distance <= 2) {
        // Large bodies of water (estuaries, lakes, seas, harbors)
        // Try to get actual depth from bathymetry data
        const bathymetryDepth = await getBathymetryDepth(lng, lat);

        // Use bathymetry if available, otherwise estimate from water class
        const waterClass = props.class || 'lake';
        const depth = bathymetryDepth !== null ? bathymetryDepth : estimateWaterDepth(waterClass);

        features.push({
          type: 'water',
          name: props.name,
          depth: depth,
          properties: props
        });
      } else if (layerName === 'road' && distance <= 2) {
        // Roads - important for HDD planning due to traffic loads and buried utilities
        const roadClass = props.class || 'tertiary';
        const utilityInfo = getRoadUtilityInfo(roadClass);
        features.push({
          type: 'road',
          name: props.name,
          width: estimateRoadWidth(roadClass),
          roadClass: roadClass,
          pavementDepth: estimateRoadPavementDepth(roadClass),
          utilityDepth: estimateRoadUtilityDepth(roadClass),
          likelyUtilities: utilityInfo.likelyUtilities,
          properties: props
        });
      } else if (layerName === 'railway' && distance <= 2) {
        // Railways - critical infrastructure requiring extra clearance
        const railwayClass = props.class || 'rail';
        features.push({
          type: 'railway',
          name: props.name,
          width: estimateRailwayWidth(railwayClass),
          railwayClass: railwayClass,
          properties: props
        });
      }
    }
  } catch (error) {
    console.error('Tilequery fetch error:', error);
  }

  return features;
}
